import { create } from 'zustand';
import { apiUpload, apiSSE } from '../lib/api-client';
import type { ImportProgress, ImportSummary } from '@range-reader/shared/api.types';

export type ImportStatus = 'idle' | 'importing' | 'complete' | 'error';

interface ImportState {
  status: ImportStatus;
  progress: ImportProgress | null;
  summary: ImportSummary | null;
  error: string | null;

  startImport: (files: FileList) => Promise<void>;
  reset: () => void;
}

// The server accepts at most 20 files per POST /api/import (see
// apps/server/src/routes/import.ts) and Cloud Run limits request bodies to
// ~32 MB by default, so we batch uploads to stay safely under both caps.
const MAX_FILES_PER_BATCH = 20;
const MAX_BYTES_PER_BATCH = 20 * 1024 * 1024; // 20 MB

function chunkFiles(files: File[]): File[][] {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const f of files) {
    const exceedsCount = current.length >= MAX_FILES_PER_BATCH;
    const exceedsSize = current.length > 0 && currentBytes + f.size > MAX_BYTES_PER_BATCH;
    if (exceedsCount || exceedsSize) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(f);
    currentBytes += f.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

const EMPTY_SUMMARY: ImportSummary = {
  filesProcessed: 0,
  handsFound: 0,
  handsAdded: 0,
  handsDuplicate: 0,
  handsSkippedByRule: {},
  handsError: 0,
  handsCorrupted: 0,
  handsMultiRunout: 0,
  elapsedMs: 0,
};

function mergeSummaries(a: ImportSummary, b: ImportSummary): ImportSummary {
  const mergedRules = { ...a.handsSkippedByRule };
  for (const [k, v] of Object.entries(b.handsSkippedByRule)) {
    mergedRules[k] = (mergedRules[k] ?? 0) + v;
  }
  return {
    filesProcessed: a.filesProcessed + b.filesProcessed,
    handsFound: a.handsFound + b.handsFound,
    handsAdded: a.handsAdded + b.handsAdded,
    handsDuplicate: a.handsDuplicate + b.handsDuplicate,
    handsSkippedByRule: mergedRules,
    handsError: a.handsError + b.handsError,
    handsCorrupted: a.handsCorrupted + b.handsCorrupted,
    handsMultiRunout: a.handsMultiRunout + b.handsMultiRunout,
    elapsedMs: a.elapsedMs + b.elapsedMs,
  };
}

async function runBatch(
  batch: File[],
  completedTotals: { handsParsed: number; handsAdded: number; handsDuplicate: number; handsError: number },
  filesProcessedBefore: number,
  filesTotal: number,
  onProgress: (p: ImportProgress) => void,
): Promise<ImportSummary> {
  const formData = new FormData();
  for (const f of batch) formData.append('files', f);
  const { jobId } = await apiUpload<{ jobId: string }>('/api/import', formData);

  return new Promise<ImportSummary>((resolve, reject) => {
    apiSSE(`/api/import/${jobId}/progress`, {
      onProgress: (data) => {
        const p = data as ImportProgress;
        onProgress({
          currentFile: p.currentFile,
          filesProcessed: filesProcessedBefore + p.filesProcessed,
          filesTotal,
          handsParsed: completedTotals.handsParsed + p.handsParsed,
          handsAdded: completedTotals.handsAdded + p.handsAdded,
          handsDuplicate: completedTotals.handsDuplicate + p.handsDuplicate,
          handsError: completedTotals.handsError + p.handsError,
        });
      },
      onComplete: (data) => resolve(data as ImportSummary),
      onError: (data) => reject(new Error((data as { error?: string }).error ?? 'Import failed')),
    });
  });
}

export const useImportStore = create<ImportState>((set) => ({
  status: 'idle',
  progress: null,
  summary: null,
  error: null,

  startImport: async (fileList: FileList) => {
    const files = Array.from(fileList);
    const batches = chunkFiles(files);
    const filesTotal = files.length;

    // Show the progress bar immediately rather than waiting for the first SSE tick
    set({
      status: 'importing',
      progress: { currentFile: '', filesProcessed: 0, filesTotal, handsParsed: 0, handsAdded: 0, handsDuplicate: 0, handsError: 0 },
      summary: null,
      error: null,
    });

    let aggregate = { ...EMPTY_SUMMARY };
    let filesProcessed = 0;
    const completedTotals = { handsParsed: 0, handsAdded: 0, handsDuplicate: 0, handsError: 0 };

    try {
      for (const batch of batches) {
        const batchSummary = await runBatch(
          batch,
          completedTotals,
          filesProcessed,
          filesTotal,
          (p) => set({ progress: p }),
        );
        aggregate = mergeSummaries(aggregate, batchSummary);
        filesProcessed += batch.length;
        completedTotals.handsParsed += batchSummary.handsFound;
        completedTotals.handsAdded += batchSummary.handsAdded;
        completedTotals.handsDuplicate += batchSummary.handsDuplicate;
        completedTotals.handsError += batchSummary.handsError;
      }

      set({
        status: 'complete',
        summary: aggregate,
        progress: {
          currentFile: '',
          filesProcessed: filesTotal,
          filesTotal,
          handsParsed: completedTotals.handsParsed,
          handsAdded: completedTotals.handsAdded,
          handsDuplicate: completedTotals.handsDuplicate,
          handsError: completedTotals.handsError,
        },
      });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  },

  reset: () => set({ status: 'idle', progress: null, summary: null, error: null }),
}));
